/**
 * AuthorizationGate — the platform-AUTHORIZATION door (both auth modes). It sits INSIDE
 * SessionProvider and gates on the resolved `GET /api/v1/me` result, not merely on the presence
 * of a B2C token (that is AuthGate's job in b2c mode). A valid credential is necessary but NOT
 * sufficient: only a resolved platform principal may see the app shell.
 *
 *   loading                → a loader (the /me fetch is in flight)
 *   principal resolved     → the app (children)
 *   error === unauthorized → AccessDenied (HTTP 403: authenticated, not authorized)
 *   error === sessionExpired → a "sign in again" screen (HTTP 401, b2c: the access token expired and
 *                            api.ts's silent re-acquire did not recover) — never a dead-end
 *   error === authRejected → a TERMINAL "we could not verify your access" screen (HTTP 401, b2c: a
 *                            fresh force-refreshed token was STILL rejected) with a Sign-out, NOT a
 *                            "sign in again" that would mint another rejected token and loop
 *   error === unavailable  → a retry screen (5xx / network: the API could not answer)
 *
 * This closes the fail-open gap where a 403 used to degrade to "show all nav", AND the availability
 * gap where an expired-token 401 stranded a returning user on "temporarily unavailable" with only a
 * Retry. Applied in BOTH compositions (b2c: AuthGate > SessionProvider > AuthorizationGate; basic:
 * SessionProvider > AuthorizationGate). No PHI here.
 */
import { type ReactNode } from "react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { AlertTriangle, Clock, ShieldAlert } from "lucide-react";
import { useSession } from "../lib/session";
import { isB2cMode } from "./authMode";
import { AuthLoader } from "./Callback";
import { AccessDenied } from "./AccessDenied";

/**
 * Definitively re-authenticate. In b2c mode a logoutRedirect ends the stale B2C session and returns
 * through a fresh sign in (the surest recovery when a token has expired/was rejected); the failure is
 * kept observable (non-PII telemetry) with a hard-nav-to-origin fail-safe so the button always acts.
 * In basic mode there is no interactive login, so returning to the origin re-bootstraps the app.
 * Mirrors AccessDenied's Sign-out handler; dynamic imports keep MSAL out of the basic-mode bundle.
 */
function handleSignInAgain(): void {
  if (isB2cMode()) {
    void import("./authService")
      .then(({ logout }) => logout())
      .catch((err) => {
        void import("./msalConfig")
          .then((m) => m.reportMsalError("sessionExpiredReauth", err))
          .catch(() => {});
        window.location.assign(window.location.origin);
      });
  } else {
    window.location.assign(window.location.origin);
  }
}

/**
 * Terminal sign-out. Unlike handleSignInAgain, this does NOT loop back into a fresh login (a
 * re-login would only mint another token the API rejects). In b2c mode it ends the B2C session
 * (logoutRedirect) with a hard-nav-to-origin fail-safe; in basic mode it returns to the origin.
 * Mirrors AccessDenied's Sign-out; dynamic imports keep MSAL out of the basic-mode bundle.
 */
function handleSignOut(): void {
  if (isB2cMode()) {
    void import("./authService")
      .then(({ logout }) => logout())
      .catch((err) => {
        void import("./msalConfig")
          .then((m) => m.reportMsalError("authRejectedSignOut", err))
          .catch(() => {});
        window.location.assign(window.location.origin);
      });
  } else {
    window.location.assign(window.location.origin);
  }
}

const outerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  padding: 24,
  fontFamily: "system-ui",
  background: "#f6f7f9",
} as const;

const cardStyle = {
  maxWidth: 460,
  width: "100%",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 32,
  textAlign: "center",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
} as const;

/**
 * The access token expired (or was rejected) and could not be silently renewed. Offer a clear re-auth
 * so a returning user is never stranded. b2c-only state (basic mode never expires a bundled credential).
 */
function SessionExpired() {
  return (
    <div style={outerStyle}>
      <div role="alert" style={cardStyle}>
        <Clock size={40} color="#b54708" style={{ margin: "0 auto 16px" }} />
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          Your session has expired
        </h1>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          Please sign in again to pick up where you left off.
        </p>
        <SkyButton appearance="primary" onClick={handleSignInAgain}>
          Sign in again
        </SkyButton>
      </div>
    </div>
  );
}

/** The API could not answer (5xx / network). Retry re-runs the whole /me load; b2c users also get a
 *  re-auth escape so they are never trapped with only Retry. */
function SessionUnavailable() {
  return (
    <div style={outerStyle}>
      <div role="alert" style={cardStyle}>
        <AlertTriangle size={40} color="#b54708" style={{ margin: "0 auto 16px" }} />
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          skyCare is temporarily unavailable
        </h1>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          We could not load your workspace. Please try again in a moment.
        </p>
        <SkyButton appearance="primary" onClick={() => window.location.reload()}>
          Retry
        </SkyButton>
        {isB2cMode() && (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              onClick={handleSignInAgain}
              style={{
                background: "none",
                border: "none",
                padding: 4,
                color: "#475467",
                fontSize: 13,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Sign in again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * TERMINAL "we could not verify your access" screen. Reached only when a FRESH (force-refreshed) b2c
 * token was still rejected by the API (authRejected). Offers a Sign out (NOT a "Sign in again" that
 * would mint yet another rejected token and re-loop) and points the user at their administrator, since
 * this signals an audience/scope/clock/provisioning problem the user cannot self-resolve. b2c-only.
 */
function AccessUnverified() {
  return (
    <div style={outerStyle}>
      <div role="alert" style={cardStyle}>
        <ShieldAlert size={40} color="#b42318" style={{ margin: "0 auto 16px" }} />
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          We could not verify your access to skyCare
        </h1>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          Your sign in worked, but we were unable to confirm your access. Please contact your
          administrator for help.
        </p>
        <SkyButton appearance="primary" onClick={handleSignOut}>
          Sign out
        </SkyButton>
      </div>
    </div>
  );
}

export function AuthorizationGate({ children }: { children: ReactNode }) {
  const { principal, loading, error } = useSession();

  if (loading) {
    return <AuthLoader label="Loading your workspace" />;
  }
  if (principal) {
    return <>{children}</>;
  }
  if (error === "unauthorized") {
    return <AccessDenied />;
  }
  if (error === "sessionExpired") {
    return <SessionExpired />;
  }
  if (error === "authRejected") {
    // Terminal: a fresh token was still rejected. No re-login affordance (it would loop).
    return <AccessUnverified />;
  }
  // error === "unavailable" (or any non-principal terminal state): fail closed to a retry screen,
  // never to the app shell.
  return <SessionUnavailable />;
}
